import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Image as ImageIcon, Edit, Trash2, ExternalLink } from "lucide-react";
import { useState } from "react";
import { useTemplateImages } from "@/hooks/useTemplateImages";
import { ImageConfigDialog } from "./ImageConfigDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ImageManagerProps {
  templateId: number;
}

export function ImageManager({ templateId }: ImageManagerProps) {
  const { images, isLoading, deleteImage, isDeleting } = useTemplateImages(templateId);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState<any | null>(null);
  const [deletingImageId, setDeletingImageId] = useState<number | null>(null);

  const handleEdit = (image: any) => {
    setEditingImage(image);
    setCreateDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setEditingImage(null);
  };

  const handleDelete = async () => {
    if (deletingImageId) {
      await deleteImage(deletingImageId);
      setDeletingImageId(null);
    }
  };

  const getImageTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      url: "URL",
      upload: "Archivo subido",
      client_logo: "Logo del cliente",
      qr_code: "Código QR",
    };
    return labels[type] || type;
  };

  const getImageTypeVariant = (type: string): "default" | "secondary" | "outline" | "destructive" => {
    const variants: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
      url: "default",
      upload: "secondary",
      client_logo: "outline",
      qr_code: "default",
    };
    return variants[type] || "default";
  };

  if (isLoading) {
    return <div className="p-4">Cargando imágenes...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Imágenes de la Plantilla</h2>
          <p className="text-sm text-muted-foreground">
            Configura las imágenes que se insertarán en el PowerPoint
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Agregar Imagen
        </Button>
      </div>

      {images.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              No hay imágenes configuradas para esta plantilla
            </p>
            <Button onClick={() => setCreateDialogOpen(true)} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Agregar primera imagen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((image) => (
            <Card key={image.id} className={!image.is_active ? "opacity-60" : ""}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ImageIcon className="h-5 w-5" />
                      {image.name}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {image.description || "Sin descripción"}
                    </CardDescription>
                  </div>
                  {!image.is_active && (
                    <Badge variant="outline" className="ml-2">
                      Inactiva
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Tipo:</span>
                    <Badge variant={getImageTypeVariant(image.image_type)}>
                      {getImageTypeLabel(image.image_type)}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Slide:</span>
                    <span className="font-medium">{image.slide_number}</span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Posición:</span>
                    <span className="font-mono text-xs">
                      ({image.position_x}, {image.position_y})
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Tamaño:</span>
                    <span className="font-mono text-xs">
                      {image.width} × {image.height}
                    </span>
                  </div>

                  {image.image_type === "url" && image.image_config?.url && (
                    <div className="pt-2 border-t">
                      <a
                        href={image.image_config.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        Ver URL <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleEdit(image)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeletingImageId(image.id)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ImageConfigDialog
        templateId={templateId}
        image={editingImage}
        open={createDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseDialog();
        }}
      />

      <AlertDialog open={deletingImageId !== null} onOpenChange={(open) => !open && setDeletingImageId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente la configuración de la imagen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
